/**
 * Mon abonnement (docs/10 §8).
 *
 * Sert aussi bien un compte gratuit qu'un abonné : l'API rend un abonnement
 * même quand il n'y en a pas — `status: null` et le plan par défaut —, ce qui
 * évite à cet écran de traiter comme une erreur l'état normal de la majorité
 * des comptes.
 *
 * Le bouton d'annulation n'apparaît que s'il y a quelque chose à annuler, et
 * annule **en fin de période** : l'abonné a payé jusque-là, et lui retirer ses
 * droits sur-le-champ reviendrait à reprendre ce qui est dû.
 */
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import {
  useCancelSubscription,
  useSubscription,
  useUsage,
  type Usage,
} from "@/features/billing/api/useBilling";
import { Alert } from "@/shared/ui/Alert";
import { Button } from "@/shared/ui/Button";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { Skeleton } from "@/shared/ui/Skeleton";

export function SubscriptionPage() {
  const { t, i18n } = useTranslation("billing");
  const subscription = useSubscription();
  const usage = useUsage();
  const cancel = useCancelSubscription();

  if (subscription.isPending) {
    return (
      <Skeleton className="mx-auto my-8 h-72 w-full max-w-3xl" label={t("subscription.title")} />
    );
  }
  if (subscription.isError) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <ErrorState error={subscription.error} />
      </div>
    );
  }

  const data = subscription.data;
  const isSubscribed = data.status !== null;

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 px-4 py-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">{t("subscription.title")}</h1>
      </header>

      <Card title={data.plan_name}>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {isSubscribed
            ? t([`subscription.status.${data.status}`, "subscription.status.active"])
            : t("subscription.noSubscription")}
        </p>

        {data.trial_ends_at !== null ? (
          <p className="mt-2 text-sm">
            {t("subscription.trialEnds", {
              date: formatDate(data.trial_ends_at, i18n.language),
            })}
          </p>
        ) : null}

        {/* L'annulation programmée passe **avant** la date de renouvellement :
            un abonné qui vient d'annuler doit lire « votre accès prend fin
            le … », pas « prochain renouvellement le … » — la même date, deux
            phrases opposées, et c'est la seconde qui serait fausse. */}
        {data.cancel_at_period_end && data.current_period_end !== null ? (
          <Alert tone="warning" className="mt-3">
            {t("subscription.endsOn", {
              date: formatDate(data.current_period_end, i18n.language),
            })}
          </Alert>
        ) : data.current_period_end !== null ? (
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            {t("subscription.renewsOn", {
              date: formatDate(data.current_period_end, i18n.language),
            })}
          </p>
        ) : null}

        <div className="mt-4 flex flex-wrap gap-3">
          <Link to="/billing/plans" className="text-brand-600 text-sm hover:underline">
            {t("subscription.seePlans")}
          </Link>
          {isSubscribed && !data.cancel_at_period_end ? (
            <Button
              variant="secondary"
              onClick={() => {
                cancel.mutate({ atPeriodEnd: true });
              }}
              disabled={cancel.isPending}
            >
              {t("subscription.cancel")}
            </Button>
          ) : null}
        </div>

        {cancel.isError ? <ErrorState error={cancel.error} /> : null}
      </Card>

      {usage.data !== undefined && usage.data.length > 0 ? (
        <Card title={t("subscription.usage")}>
          <ul className="space-y-3">
            {usage.data.map((item) => (
              <li key={item.feature_code}>
                <UsageRow usage={item} />
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}

/**
 * La consommation d'une capacité comptée.
 *
 * `limit === null` signifie « illimité » : la barre disparaît alors, plutôt que
 * d'afficher une jauge sans fond. Une division par `null` traité comme `0`
 * donnerait `Infinity`, et une barre remplie à l'infini se lit « vous êtes au
 * maximum » — exactement l'inverse de la vérité.
 */
function UsageRow({ usage }: { usage: Usage }) {
  const { t } = useTranslation("billing");
  const limit = usage.limit;
  // `limit === null` = illimité, `limit === 0` = rien d'autorisé. Les deux
  // sortent de la division : le premier n'a pas de jauge, le second la
  // remplirait à `Infinity`.
  const unlimited = limit === null;
  const percent = limit === null || limit === 0 ? 0 : Math.min(100, (usage.used / limit) * 100);

  return (
    <div>
      <div className="flex justify-between gap-4 text-sm">
        <span>{usage.label}</span>
        <span className="text-slate-600 dark:text-slate-400">
          {unlimited
            ? t("subscription.usedUnlimited", { usedValue: usage.used })
            : t("subscription.usedOf", { usedValue: usage.used, limitValue: usage.limit })}
        </span>
      </div>
      {unlimited ? null : (
        <div className="mt-1 h-2 rounded bg-slate-200 dark:bg-slate-700">
          <div
            className="bg-brand-500 h-2 rounded"
            style={{ width: `${String(percent)}%` }}
            role="presentation"
          />
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: "long" }).format(new Date(iso));
}
